import React, { useState, useRef, useEffect, useMemo } from 'react'
import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  tablePlugin,
  linkPlugin,
  linkDialogPlugin,
  diffSourcePlugin,
  markdownShortcutPlugin,
  toolbarPlugin,
  UndoRedo,
  BoldItalicUnderlineToggles,
  BlockTypeSelect,
  ListsToggle,
  InsertThematicBreak,
  InsertTable,
  CreateLink,
  DiffSourceToggleWrapper,
  Separator,
} from '@mdxeditor/editor'

// The secondary formatting controls. Rendered inline when the field is wide and,
// in a separate copy, inside the overflow menu when it is narrow. A container
// query in index.css shows exactly one copy at a time.
function SecondaryControls() {
  return (
    <>
      <BlockTypeSelect />
      <ListsToggle />
      <Separator />
      <InsertThematicBreak />
      <InsertTable />
      <CreateLink />
    </>
  )
}

// A "⋮" button that reveals the secondary controls in a dropdown panel. Shown
// only on narrow widths (see the container query in index.css).
function OverflowMenu({ children }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  return (
    <div className="toolbar-overflow-menu" ref={ref}>
      <button
        type="button"
        className="toolbar-overflow-trigger"
        aria-label="More formatting options"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        &#8942;
      </button>
      {open && <div className="toolbar-overflow-panel">{children}</div>}
    </div>
  )
}

function ToolbarContents() {
  // DiffSourceToggleWrapper adds the rich-text / source / diff switch and only
  // renders the formatting controls while in rich-text mode.
  return (
    <DiffSourceToggleWrapper>
      <UndoRedo />
      <Separator />
      <BoldItalicUnderlineToggles />
      <div className="toolbar-overflow-inline">
        <Separator />
        <SecondaryControls />
      </div>
      <OverflowMenu>
        <SecondaryControls />
      </OverflowMenu>
    </DiffSourceToggleWrapper>
  )
}

const toolbar = toolbarPlugin({
  toolbarClassName: 'custom-toolbar',
  toolbarContents: () => <ToolbarContents />,
})

const contentPlugins = [
  headingsPlugin(),
  listsPlugin(),
  quotePlugin(),
  thematicBreakPlugin(),
  tablePlugin(),
  linkPlugin(),
  linkDialogPlugin(),
  markdownShortcutPlugin(),
]

export default function App({ markdown = '', definition = {}, onChange }) {
  const readOnly = definition.enabled === false
  const showLabel = definition.label && !definition.hideLabel

  // Capture the value first loaded into the editor so "diff" mode compares the
  // current content against the original document value.
  const initialMarkdown = useRef(markdown).current

  const plugins = useMemo(
    () =>
      readOnly
        ? contentPlugins
        : [
            toolbar,
            ...contentPlugins,
            diffSourcePlugin({ viewMode: 'rich-text', diffMarkdown: initialMarkdown }),
          ],
    [readOnly, initialMarkdown]
  )

  return (
    <>
      {showLabel && (
        <label style={{ display: 'block', marginBottom: 4, color: definition.mandatory ? '#d32f2f' : 'inherit' }}>
          {definition.label}{definition.mandatory ? ' *' : ''}
        </label>
      )}
      <div style={{ border: definition.hideBorder ? 'none' : '1px solid #ddd', borderRadius: 6, padding: 12, background: '#f0f0f3' }}>
        <MDXEditor markdown={markdown} readOnly={readOnly} onChange={onChange} plugins={plugins} />
      </div>
    </>
  )
}
